import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import axios from "axios";

const Sales = () => {
  // State management
  const [allSales, setAllSales] = useState([]); // Store all sales data
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    branch: "",
    cashier: ""
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [branchPagination, setBranchPagination] = useState({
    page: 1,
    limit: 50,
    hasMore: true
  });
  const [loading, setLoading] = useState({
    sales: false,
    branches: false,
    cashiers: false,
    moreBranches: false
  });
  const [branches, setBranches] = useState([]);
  const [branchCashiers, setBranchCashiers] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  
  const { currentUser } = useSelector((state) => state.user);

  // Format date and time
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Fetch branches with pagination
  const fetchBranches = useCallback(async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoading(prev => ({ ...prev, moreBranches: true }));
      } else {
        setLoading(prev => ({ ...prev, branches: true }));
      }
      setError(null);
      
      const params = new URLSearchParams({
        page,
        limit: branchPagination.limit
      }).toString();
      
      const url = currentUser.role === "superadmin" 
        ? `/api/branch/branch?${params}`
        : `/api/branch/getbranch/${currentUser.username}?${params}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch branches');
      
      const data = await response.json();
      const branchesData = data?.branches || data || [];
      
      if (isLoadMore) {
        setBranches(prev => [...prev, ...branchesData]);
      } else {
        setBranches(branchesData);
      }
      
      setBranchPagination(prev => ({
        ...prev,
        page,
        hasMore: branchesData.length >= branchPagination.limit
      }));
    } catch (err) {
      setError(err.message);
      if (!isLoadMore) setBranches([]);
    } finally {
      if (isLoadMore) {
        setLoading(prev => ({ ...prev, moreBranches: false }));
      } else {
        setLoading(prev => ({ ...prev, branches: false }));
      }
    }
  }, [currentUser, branchPagination.limit]);

  // Load more branches
  const loadMoreBranches = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    fetchBranches(branchPagination.page + 1, true);
  }, [branchPagination.page, fetchBranches]);

  // Fetch cashiers when branch changes
  const fetchCashiers = useCallback(async (branch) => {
    if (!branch) {
      setBranchCashiers([]);
      return;
    }

    try {
      setLoading(prev => ({ ...prev, cashiers: true }));
      setError(null);
      
      const response = await fetch(`/api/user/getusers/${branch}`);
      if (!response.ok) throw new Error('Failed to fetch cashiers');
      
      const data = await response.json();
      setBranchCashiers(Array.isArray(data) ? data : data?.users || []);
    } catch (err) {
      setError(err.message);
      setBranchCashiers([]);
    } finally {
      setLoading(prev => ({ ...prev, cashiers: false }));
    }
  }, []);

  // Fetch all sales data without pagination
  const fetchSales = useCallback(async () => {
    if (!filters.branch) return;

    try {
      setLoading(prev => ({ ...prev, sales: true }));
      setError(null);
      
      // Only include filter parameters, no pagination
      const params = new URLSearchParams({
        branch: filters.branch,
        ...(filters.cashier && { cashier: filters.cashier }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      });

      console.log('Fetching all sales with params:', params.toString()); // Debug log

      const response = await axios.get(`/api/sales/getSales?${params.toString()}`);
      const data = response.data;
      
      console.log('Sales API response:', data); // Debug log
      
      // Handle both array directly or nested in data object
      const salesData = Array.isArray(data) ? data : (data.sales || data.data || []);
      setAllSales(salesData);
      
      // Update total count and reset to page 1
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: salesData.length
      }));
    } catch (err) {
      console.error('Sales fetch error:', err); // Debug log
      setError(err.response?.data?.message || err.message);
      setAllSales([]);
    } finally {
      setLoading(prev => ({ ...prev, sales: false }));
    }
  }, [filters]);

  // Get paginated sales for current page (frontend pagination)
  const paginatedSales = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    return allSales.slice(startIndex, endIndex);
  }, [allSales, pagination.page, pagination.limit]);

  // Load branches on mount
  useEffect(() => {
    fetchBranches(1);
  }, [fetchBranches]);

  // Load cashiers when branch changes
  useEffect(() => {
    fetchCashiers(filters.branch);
  }, [filters.branch, fetchCashiers]);

  // Fetch sales when filters change (not page changes)
  useEffect(() => {
    if (filtersApplied) {
      fetchSales();
    }
  }, [filters, fetchSales, filtersApplied]);

  // Calculate totals from all sales (not just current page)
  const { totalWinning, totalCut, totalTransactions } = useMemo(() => {
    return allSales.reduce(
      (acc, sale) => {
        // Handle the winners array structure from your JSON
        const winner = sale.winners && sale.winners[0];
        return {
          totalWinning: acc.totalWinning + (winner?.won || 0),
          totalCut: acc.totalCut + (winner?.cut || 0),
          totalTransactions: acc.totalTransactions + 1
        };
      },
      { totalWinning: 0, totalCut: 0, totalTransactions: 0 }
    );
  }, [allSales]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters - Prevent page refresh and reset pagination
  const applyFilters = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!filters.branch) {
      setError("Please select a branch");
      return;
    }
    setError(null);
    // Reset to page 1 when applying new filters
    setPagination(prev => ({ ...prev, page: 1 }));
    setFiltersApplied(true);
  };

  // Handle form submission to prevent refresh
  const handleFormSubmit = (e) => {
    e.preventDefault();
    applyFilters(e);
  };

  // Clear all filters
  const clearFilters = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFilters({
      startDate: "",
      endDate: "",
      branch: "",
      cashier: ""
    });
    setFiltersApplied(false);
    setAllSales([]);
    setError(null);
    // Reset pagination when clearing filters
    setPagination(prev => ({ ...prev, page: 1, total: 0 }));
  };

  // Pagination controls - Frontend pagination
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const handlePageChange = (newPage, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (newPage >= 1 && newPage <= totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  if (loading.sales && filtersApplied) return <LoadingSpinner fullPage />;
  if (error && !loading.sales) return <ErrorMessage message={error} onRetry={clearFilters} />;

  return (
    <div className="tw-container tw-mx-auto tw-p-4 tw-mt-10">
      {/* Header */}
      <div className="tw-text-green-700 tw-font-bold tw-text-3xl tw-mb-6">
        Balance <span className="tw-text-pink-600">(${currentUser.balance?.toLocaleString()})</span>
      </div>

      {/* Filter Controls */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4 tw-mb-6">
        <form onSubmit={handleFormSubmit} className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md text-base"
            />
          </div>
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md text-base"
            />
          </div>
          <div className="tw-relative">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Branch
            </label>
            <div className="tw-relative">
              <select
                name="branch"
                value={filters.branch}
                onChange={handleFilterChange}
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md text-base"
                disabled={loading.branches}
              >
                <option value="">Select Branch</option>
                {branches.map((branch) => (
                  <option key={branch._id || branch.name} value={branch.name}>
                    {branch.name}
                  </option>
                ))}
              </select>
              {branchPagination.hasMore && (
                <button
                  type="button"
                  onClick={loadMoreBranches}
                  disabled={loading.moreBranches}
                  className="tw-absolute tw-right-1 tw-bottom-1 tw-px-2 tw-py-1 tw-bg-blue-600 tw-text-white tw-text-xs tw-rounded hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                  {loading.moreBranches ? 'Loading...' : 'Load More'}
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Cashier
            </label>
            <select
              name="cashier"
              value={filters.cashier}
              onChange={handleFilterChange}
              disabled={!filters.branch || loading.cashiers}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md text-base"
            >
              <option value="">Select Cashier</option>
              {branchCashiers.map((cashier) => (
                <option key={cashier._id} value={cashier.username}>
                  {cashier.username}
                </option>
              ))}
            </select>
          </div>
        </form>
        
        <div className="tw-flex tw-justify-between tw-mt-4">
          <div className="tw-flex tw-space-x-3">
            <button
              type="button"
              onClick={clearFilters}
              className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-gray-700 hover:tw-bg-gray-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={applyFilters}
              disabled={!filters.branch || loading.cashiers}
              className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 disabled:tw-opacity-50"
            >
              {loading.sales ? "Loading..." : "Filter"}
            </button>
          </div>
        </div>
      </div>

      {/* Loading indicator for sales */}
      {loading.sales && (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-8 tw-text-center">
          <div className="tw-inline-block tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
          <p className="tw-text-gray-500 tw-mt-2">Loading sales data...</p>
        </div>
      )}

      {/* Summary Cards */}
      {filtersApplied && allSales.length > 0 && !loading.sales && (
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
            <h3 className="tw-font-bold tw-text-purple-600">
              Total Winning: ${totalWinning.toFixed(2)}
            </h3>
          </div>
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
            <h3 className="tw-font-bold tw-text-purple-600">
              Total Cut: ${totalCut.toFixed(2)}
            </h3>
          </div>
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
            <h3 className="tw-font-bold tw-text-purple-600">
              Total Transactions: {totalTransactions}
            </h3>
          </div>
        </div>
      )}

      {/* Pagination Info */}
      {filtersApplied && !loading.sales && allSales.length > 0 && (
        <div className="tw-mb-4 tw-text-sm tw-text-gray-600 tw-text-center">
          Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
        </div>
      )}

      {/* Sales Table */}
      {filtersApplied && !loading.sales ? (
        paginatedSales.length > 0 ? (
          <>
            <div className="tw-overflow-x-auto tw-bg-white tw-rounded-lg tw-shadow-md">
              <table className="tw-min-w-full">
                <thead className="tw-bg-blue-800">
                  <tr>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Date
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Time
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Bet
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Player
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Total Won
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Cut
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Won
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      #Call
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Winners
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Branch
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Cashier
                    </th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSales.map((sale) => {
                    const winner = sale.winners && sale.winners[0];
                    return (
                      <tr key={sale._id} className="tw-border-b tw-border-gray-200 hover:tw-bg-gray-50">
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          {formatDate(sale.createdAt)}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          {formatTime(sale.createdAt)}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          ${winner?.bet?.toLocaleString() || '0'}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          {winner?.player || '-'}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          ${winner?.total?.toLocaleString() || '0'}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          ${winner?.cut?.toLocaleString() || '0'}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          ${winner?.won?.toLocaleString() || '0'}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          {winner?.call || '-'}
                        </td>
                        <td className="tw-px-4 tw-py-3">
                          {winner?.winner?.join(", ") || "-"}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          {winner?.branch || '-'}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          {winner?.cashier || '-'}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                          ${winner?.balance?.toLocaleString() || '0'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="tw-flex tw-justify-center tw-items-center tw-mt-6 tw-space-x-2">
                <button
                  type="button"
                  onClick={(e) => handlePageChange(pagination.page - 1, e)}
                  disabled={pagination.page === 1}
                  className="tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-rounded-md tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                  Previous
                </button>

                <div className="tw-flex tw-space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        type="button"
                        onClick={(e) => handlePageChange(pageNum, e)}
                        className={`tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md ${
                          pagination.page === pageNum
                            ? "tw-bg-blue-600 tw-text-white"
                            : "tw-bg-white tw-text-gray-700 tw-border hover:tw-bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={(e) => handlePageChange(pagination.page + 1, e)}
                  disabled={pagination.page === totalPages}
                  className="tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-rounded-md tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-8 tw-text-center">
            <p className="tw-text-gray-500">
              No sales found for the selected filters
            </p>
          </div>
        )
      ) : !filtersApplied ? (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-8 tw-text-center">
          <p className="tw-text-gray-500">
            Please apply filters to view sales data
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default Sales;